'use client';

import { Close } from '@1debit/icons';
import {
  trackFloatingDisclaimerDismissed,
  trackFloatingDisclaimerShown,
} from 'components/analytics/eventHandlerTracking';
import { useFloatingDisclaimerShownTracking } from 'components/analytics/hooks/useFloatingDisclaimerShownTracking';
import Button from 'components/shared/Buttons/BaseButton';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { toDocumentWithEmbeddedEntries } from 'libs/contentful/toDocumentWithEmbeddedEntries';
import { DisclosureBannerProps } from './types';
import { useEffect, useState } from 'react';
import { cn, getColorway } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { usePage } from '@/contexts/PageProvider';
import { getEntryNativeFields } from 'utils/entryNativeFields';

const DisclosureBanner = (props: DisclosureBannerProps) => {
  const { copy, hideWhileHeroVisible } = getEntryNativeFields(props);
  // Entry-native copy arrives as a bare resolved Document; relink its embedded entries
  // (disclosure superscripts, inline modals) into the { json, links } shape RichTextCopy
  // reads - the same shared converter Note and the other Entry-native modules use.
  const copyDoc = copy
    ? toDocumentWithEmbeddedEntries(copy, { parentTypename: 'DisclosureBanner', fieldName: 'copy' })
    : undefined;
  const { visualSettings } = usePage();
  const [dismissed, setDismissed] = useState(false);
  // When hideWhileHeroVisible is set, start hidden and let the IntersectionObserver control visibility.
  const [heroPastViewport, setHeroPastViewport] = useState(!hideWhileHeroVisible);
  const trackBannerShown = useFloatingDisclaimerShownTracking();

  useEffect(() => {
    if (!hideWhileHeroVisible || dismissed) return;

    const hero = document.querySelector('[data-hero-module]');
    if (!hero) {
      // No hero on this page — show the banner. Deferred so setState is not
      // called synchronously in the effect body.
      queueMicrotask(() => setHeroPastViewport(true));
      return;
    }

    // Account for the sticky site navigation overlaying the top of the hero
    const navHeight = document.querySelector('[data-header-nav]')?.getBoundingClientRect().height ?? 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHeroPastViewport(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: `-${navHeight}px 0px 0px 0px` },
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, [hideWhileHeroVisible, dismissed]);

  useEffect(() => {
    if (!heroPastViewport) return;
    trackBannerShown();
    trackFloatingDisclaimerShown();
  }, [heroPastViewport, trackBannerShown]);

  const handleClose = () => {
    setDismissed(true);
    trackFloatingDisclaimerDismissed();

    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('disclosureBannerDismissed'));
  };

  if (dismissed) return null;

  return (
    <div
      className={cn(
        'w-full min-h-min sm:h-14 box-border',
        'shadow-kale/5 shadow-[0_-10px_20px]',
        'fixed bottom-0 right-0 z-25 ',
        'transition-transform duration-300 ease-in-out',
        !heroPastViewport && 'translate-y-full',
        getColorway(visualSettings?.pageColor),
      )}
      data-disclosure-banner>
      <div
        className="content-wrapper max-sm:px-3 py-1 md:py-4 text-legal font-sans flex gap-3 md:gap-6 items-center justify-between h-full box-border"
        {...liveUpdateAttrs(props.sys?.id, 'copy')}>
        <RichTextCopy copy={copyDoc} />
        <div className="flex">
          <Button variant="icon" label="Close" onClick={handleClose} className={'p-1'}>
            <Close />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DisclosureBanner;
